import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Gift, Loader2, CheckCircle2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const todayStr = () => new Date().toISOString().slice(0, 10);
const maxStr = () => new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);

export default function RedeemGift() {
  const { code } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [gift, setGift] = useState(undefined);
  const [date, setDate] = useState(todayStr());
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/gifts/${code}`).then((r) => setGift(r.data)).catch(() => setGift(null));
  }, [code]);

  useEffect(() => {
    if (!gift || gift.status !== "active" || !date) return;
    setLoadingSlots(true);
    setTime("");
    api.get(`/practitioners/${gift.practitioner_id}/slots`, { params: { date } })
      .then((r) => setSlots(r.data.slots)).catch(() => setSlots([])).finally(() => setLoadingSlots(false));
  }, [gift, date]);

  const redeem = async () => {
    setBusy(true);
    try {
      await api.post(`/gifts/${code}/redeem`, { date, time });
      toast.success("Your gifted session is booked ✦");
      navigate("/bookings");
    } catch (err) {
      toast.error(apiError(err));
      setBusy(false);
    }
  };

  if (gift === undefined) return <div className="py-32 text-center text-muted-foreground">Opening your gift…</div>;
  if (gift === null) {
    return (
      <div className="silk-hero min-h-[70vh] flex items-center justify-center px-5">
        <div className="silk-card rounded-lg p-10 max-w-md text-center" data-testid="gift-not-found">
          <h1 className="font-display text-3xl text-foreground mb-3">Gift not found</h1>
          <p className="text-sm text-muted-foreground">This gift link isn't valid — double-check the link in your email.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="silk-hero min-h-[70vh] flex items-center justify-center px-5 py-16">
      <div className="w-full max-w-lg silk-card rounded-lg p-8 sm:p-10" data-testid="redeem-gift-page">
        <div className="w-14 h-14 rounded-full border-2 border-primary text-primary flex items-center justify-center mb-6 mx-auto">
          <Gift size={24} />
        </div>
        <h1 className="font-display text-4xl text-foreground text-center mb-2">A gift for {gift.recipient_name}</h1>
        <p className="text-sm text-muted-foreground text-center mb-6">
          <span className="text-foreground">{gift.purchaser_name}</span> has gifted you{" "}
          <span className="text-primary">{gift.service.name}</span> with{" "}
          <span className="text-foreground">{gift.business_name}</span> — fully paid.
        </p>
        {gift.message && (
          <blockquote className="border-l-2 border-primary/60 pl-4 mb-8 text-sm text-muted-foreground italic" data-testid="gift-message">
            “{gift.message}” <span className="not-italic text-xs">— {gift.purchaser_name}</span>
          </blockquote>
        )}

        {gift.status === "redeemed" ? (
          <div className="text-center" data-testid="gift-already-redeemed">
            <CheckCircle2 size={28} className="text-primary mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">This gift has already been redeemed. Find the session in My Bookings.</p>
          </div>
        ) : !user ? (
          <div className="text-center space-y-3" data-testid="gift-signin-prompt">
            <p className="text-sm text-muted-foreground">Sign in — or create a free account — to pick your time.</p>
            <Button data-testid="gift-login-btn" className="rounded-full px-8 w-full h-11"
              onClick={() => navigate("/login", { state: { from: `/redeem/${code}` } })}>Sign in</Button>
            <Button variant="outline" data-testid="gift-register-btn" className="rounded-full px-8 w-full h-11"
              onClick={() => navigate("/register", { state: { from: `/redeem/${code}` } })}>Create a free account</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <input type="date" data-testid="redeem-date-input" value={date} min={todayStr()} max={maxStr()}
              onChange={(e) => setDate(e.target.value)}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm text-foreground" />
            <div data-testid="redeem-slots">
              {loadingSlots ? (
                <p className="text-sm text-muted-foreground">Loading times…</p>
              ) : slots.length === 0 ? (
                <p className="text-sm text-muted-foreground">No times available this day — try another date.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {slots.map((s) => (
                    <button key={s} type="button" data-testid={`redeem-slot-${s.replace(":", "")}`} onClick={() => setTime(s)}
                      className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${time === s ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button data-testid="redeem-confirm-btn" disabled={!time || busy} onClick={redeem} className="w-full h-11 rounded-full">
              {busy ? <Loader2 size={15} className="animate-spin mr-2" /> : null}
              {busy ? "Booking…" : "Book my gifted session"}
            </Button>
            <p className="text-xs text-muted-foreground text-center">Nothing to pay — {gift.purchaser_name} took care of it ♥</p>
          </div>
        )}
      </div>
    </div>
  );
}
